'use client'

import { useIsMobileOrTabletClient } from '@/hooks/common/use-device-type'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { EffectCreative } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useScrollDirectionNew } from '@/hooks/common/use-scroll-direction-new'
import { useStoryModeData } from '@/providers/setting-provider'
import type { TStoryItem, TStoryModePageType } from '@/types/story-mode'

import 'swiper/css'
import 'swiper/css/effect-creative'
import { getTagPositionStyle } from './story-tag-position'

// ─── Constants ───────────────────────────────────────────────────────────────
const LONG_PRESS_THRESHOLD = 150 // ms - thời gian giữ để kích hoạt pause
const DEFAULT_DURATION = 5000 // ms
const VIDEO_FALLBACK_DURATION = 15000 // ms - khi chưa đọc được độ dài video
const PROGRESS_UPDATE_INTERVAL = 50 // ms
const priceFormatter = new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
})

// ─── Image Optimization ──────────────────────────────────────────────────────
// Append ?aio=w-{width} for server-side resizing via n7media.
// mcdn.coolmate.me does NOT honor any resize query param (verified empirically —
// ?width=/&aio=w- are silently ignored there, always returns the full original).
// The same files are mirrored on n7media under /uploads/ instead of /image/
// (same convention ImageHelper.getImageUrl already uses), and n7's ?aio=w-
// resize does work — so route mcdn URLs through n7media instead.
const optimizeImg = (url: string, width: number): string => {
  if (!url) return url

  let targetUrl = url
  if (url.includes('mcdn.coolmate.me')) {
    targetUrl = url
      .replace('mcdn.coolmate.me', 'n7media.coolmate.me')
      .replace('/image/', '/uploads/')
  } else if (!url.includes('n7media.coolmate.me')) {
    return url
  }

  const separator = targetUrl.includes('?') ? '&' : '?'
  return `${targetUrl}${separator}aio=w-${width}`
}

// ─── Component ─────────────────────────────────────────────────────────────
type StoryModeProps = {
  /** Setting trang chủ chứa story (cấu hình ở admin, field `story_mode`) */
  pageType: TStoryModePageType
  /** Class cho div bọc ngoài — không render gì (kể cả wrapper) khi không có story */
  wrapperClassName?: string
}

export function StoryMode({ pageType, wrapperClassName }: StoryModeProps) {
  const storiesData = useStoryModeData(pageType)
  // startProgressTimer được memo với deps rỗng → đọc stories qua ref
  const storiesRef = useRef<TStoryItem[]>(storiesData)
  useEffect(() => {
    storiesRef.current = storiesData
  }, [storiesData])
  const { hasScrolled } = useScrollDirectionNew(50)
  const isMobile = useIsMobileOrTabletClient()

  // Story viewer state
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isMuted, setIsMuted] = useState(false)

  // Avatar list scroll state
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const avatarListRef = useRef<HTMLDivElement>(null)

  // Refs
  const swiperRef = useRef<SwiperType | null>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isLongPressRef = useRef(false)
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )
  const progressRef = useRef(0) // để tránh stale closure
  const progressLinesRef = useRef<(HTMLDivElement | null)[]>([])
  const isMutedRef = useRef(isMuted)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isViewerOpenRef = useRef(false)

  // ─── Video Control ─────────────────────────────────────────────────────────
  const playActiveVideo = useCallback((index: number) => {
    const video = videoRefs.current[index]
    if (video) {
      video.currentTime = 0
      video.muted = isMutedRef.current
      video.play().catch(() => {})
    }
  }, [])

  const pauseAllVideos = useCallback(() => {
    videoRefs.current.forEach((video) => {
      if (video) {
        video.pause()
      }
    })
  }, [])

  // ─── Progress Bar Logic (Custom thay vì dùng onAutoplayTimeLeft) ─────────
  const startProgressTimer = useCallback(
    (index: number) => {
      // Clear timer cũ
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }

      const story = storiesRef.current[index]
      // Story video không đặt thời lượng → progress bám theo thời gian phát
      // của video (tự đúng khi buffer/pause); ảnh mặc định 5s
      const followVideo = story?.type === 'video' && !story.duration
      const duration =
        story?.duration ||
        (followVideo ? VIDEO_FALLBACK_DURATION : DEFAULT_DURATION)
      const startTime = Date.now() - (progressRef.current / 100) * duration

      progressIntervalRef.current = setInterval(() => {
        // Guard: nếu viewer đã đóng thì dừng
        if (!isViewerOpenRef.current) {
          clearInterval(progressIntervalRef.current!)
          return
        }

        const video = videoRefs.current[index]
        const videoDuration = video?.duration
        const newProgress =
          followVideo &&
          video &&
          videoDuration &&
          Number.isFinite(videoDuration)
            ? video.ended
              ? 100
              : Math.min((video.currentTime / videoDuration) * 100, 100)
            : Math.min(((Date.now() - startTime) / duration) * 100, 100)
        progressRef.current = newProgress

        // Cập nhật DOM trực tiếp để tránh re-render toàn bộ component
        if (progressLinesRef.current[index]) {
          progressLinesRef.current[index]!.style.width = `${newProgress}%`
        }

        if (newProgress >= 100) {
          clearInterval(progressIntervalRef.current!)
          // Auto chuyển slide tiếp
          if (swiperRef.current) {
            if (index < storiesRef.current.length - 1) {
              swiperRef.current.slideNext()
            } else {
              // Hết story cuối → đóng viewer
              closeViewer()
            }
          }
        }
      }, PROGRESS_UPDATE_INTERVAL)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const stopProgressTimer = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
    }
  }, [])

  // ─── Viewer Open/Close ─────────────────────────────────────────────────────
  const openViewer = useCallback(
    (startIndex = 0) => {
      // Clear any pending open timer from a previous rapid open/close
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current)
      }

      setIsViewerOpen(true)
      isViewerOpenRef.current = true
      setActiveIndex(startIndex)
      progressRef.current = 0

      // Delay để Swiper render xong
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null
        if (!isViewerOpenRef.current) return // guard: đã đóng trước khi timeout fire

        if (swiperRef.current) {
          swiperRef.current.slideTo(startIndex, 0)
        }
        playActiveVideo(startIndex)
        startProgressTimer(startIndex)
      }, 100)
    },
    [playActiveVideo, startProgressTimer]
  )

  const closeViewer = useCallback(() => {
    // Clear pending open timer to prevent ghost timer
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }

    isViewerOpenRef.current = false
    setIsViewerOpen(false)
    setIsPaused(false)
    stopProgressTimer()
    pauseAllVideos()
    progressRef.current = 0
  }, [stopProgressTimer, pauseAllVideos])

  // ─── Long Press Handlers ───────────────────────────────────────────────────
  const handlePressStart = useCallback(() => {
    isLongPressRef.current = false

    longPressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true
      setIsPaused(true)

      // Pause autoplay + video
      stopProgressTimer()
      const video = videoRefs.current[activeIndex]
      if (video) video.pause()
    }, LONG_PRESS_THRESHOLD)
  }, [activeIndex, stopProgressTimer])

  const handlePressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }

    if (isLongPressRef.current) {
      // Đây là long press → resume
      setIsPaused(false)
      const video = videoRefs.current[activeIndex]
      if (video) video.play().catch(() => {})
      startProgressTimer(activeIndex)
      isLongPressRef.current = false
    }
  }, [activeIndex, startProgressTimer])

  // ─── Navigation (chỉ khi không phải long press) ───────────────────────────
  const handleTapLeft = useCallback(() => {
    if (isLongPressRef.current || isPaused) return
    swiperRef.current?.slidePrev()
  }, [isPaused])

  const handleTapRight = useCallback(() => {
    if (isLongPressRef.current || isPaused) return
    swiperRef.current?.slideNext()
  }, [isPaused])

  // ─── Slide Change Handler ─────────────────────────────────────────────────
  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      const newIndex = swiper.realIndex
      setActiveIndex(newIndex)
      progressRef.current = 0

      // Pause tất cả video, chỉ play video hiện tại
      pauseAllVideos()
      playActiveVideo(newIndex)
      startProgressTimer(newIndex)
    },
    [pauseAllVideos, playActiveVideo, startProgressTimer]
  )

  // ─── Keyboard support ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isViewerOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          swiperRef.current?.slidePrev()
          break
        case 'ArrowRight':
          swiperRef.current?.slideNext()
          break
        case 'Escape':
          closeViewer()
          break
        case ' ':
          e.preventDefault()
          if (isPaused) {
            setIsPaused(false)
            videoRefs.current[activeIndex]?.play().catch(() => {})
            startProgressTimer(activeIndex)
          } else {
            setIsPaused(true)
            stopProgressTimer()
            videoRefs.current[activeIndex]?.pause()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isViewerOpen,
    isPaused,
    activeIndex,
    closeViewer,
    startProgressTimer,
    stopProgressTimer,
  ])

  // ─── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopProgressTimer()
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
      }
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current)
      }
    }
  }, [stopProgressTimer])

  // ─── Prevent body scroll when viewer is open ──────────────────────────────
  useEffect(() => {
    if (isViewerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isViewerOpen])

  // ─── Check avatar list scroll state on mount/resize ───────────────────────
  useEffect(() => {
    const el = avatarListRef.current
    if (!el) return

    const checkScroll = () => {
      setCanScrollLeft(el.scrollLeft > 8)
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8)
    }

    // Check on mount
    checkScroll()

    // Re-check on resize
    const observer = new ResizeObserver(checkScroll)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  if (storiesData.length === 0) return null

  return (
    <div className={wrapperClassName}>
      {/* ═══════════════════════════════════════════════════════════════════════
          PHẦN 1: AVATAR CIRCLES (Hiển thị trên Header — Scrollable như Instagram)
          ═══════════════════════════════════════════════════════════════════════ */}
      <div
        className={`w-full overflow-hidden text-center shadow-[0_-4px_6px_rgba(0,0,0,0.1)] transition-all duration-300 ease-in-out md:shadow-none ${
          hasScrolled && isMobile
            ? 'max-h-0 py-0 opacity-0'
            : 'max-h-[500px] py-2 opacity-100'
        }`}
        aria-hidden={hasScrolled && isMobile}
      >
        <div className='relative mx-auto h-full w-full max-w-full px-4 sm:px-6 md:px-4 lg:px-8 2xl:px-12'>
          {/* Gradient fade trái */}
          {canScrollLeft && (
            <div className='pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-12 bg-gradient-to-r from-white to-transparent' />
          )}
          {/* Gradient fade phải */}
          {canScrollRight && (
            <div className='pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-12 bg-gradient-to-l from-white to-transparent' />
          )}

          {/* Nút scroll trái */}
          {canScrollLeft && (
            <button
              onClick={() => {
                avatarListRef.current?.scrollBy({
                  left: -240,
                  behavior: 'smooth',
                })
              }}
              className='story-nav-arrow story-nav-arrow--left'
              aria-label='Scroll trái'
            >
              <svg
                className='h-4 w-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2.5}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </button>
          )}

          {/* Nút scroll phải */}
          {canScrollRight && (
            <button
              onClick={() => {
                avatarListRef.current?.scrollBy({
                  left: 240,
                  behavior: 'smooth',
                })
              }}
              className='story-nav-arrow story-nav-arrow--right'
              aria-label='Scroll phải'
            >
              <svg
                className='h-4 w-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2.5}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </button>
          )}

          {/* Scrollable list */}
          <div
            ref={avatarListRef}
            className='story-avatar-scroll'
            onScroll={() => {
              const el = avatarListRef.current
              if (!el) return
              setCanScrollLeft(el.scrollLeft > 8)
              setCanScrollRight(
                el.scrollLeft < el.scrollWidth - el.clientWidth - 8
              )
            }}
          >
            <div className='flex items-center gap-3 md:justify-center md:gap-6'>
              {storiesData.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => openViewer(index)}
                  className='group flex flex-shrink-0 flex-col items-center gap-1.5'
                  ga-tracking-value={`story_view`}
                  ga-tracking-label={item.id}
                >
                  {/* Avatar Ring: gradient border */}
                  <div
                    className='h-[54px] w-[54px] flex-shrink-0 cursor-pointer rounded-full p-[2px] transition-transform duration-200 hover:scale-[1.08] md:h-[64px] md:w-[64px]'
                    style={{
                      background:
                        'linear-gradient(135deg, hsl(var(--primary-500)) 0%, #a855f7 100%)',
                    }}
                  >
                    {/* Inner: white border + shimmer skeleton */}
                    <div className='relative h-full w-full overflow-hidden rounded-full bg-white p-[2px]'>
                      <div className='absolute inset-[2px] animate-pulse rounded-full bg-gray-200' />
                      <Image
                        src={optimizeImg(item.preview, 128)}
                        alt={item.id}
                        width={64}
                        height={64}
                        priority={index < 5}
                        loading={index < 5 ? undefined : 'lazy'}
                        className='relative z-[1] block h-full w-full rounded-full object-cover transition-transform duration-200 group-hover:scale-105'
                      />
                    </div>
                  </div>
                  {/* Label tạm thời ẩn */}
                  <span className='hidden max-w-[68px] truncate text-[11px] leading-tight text-gray-600 group-hover:text-gray-900'>
                    {item.id}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PHẦN 2: STORY VIEWER (Fullscreen Modal — Instagram Desktop Style)
          ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {isViewerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='story-viewer-overlay'
          >
            {/* Nút đóng (nằm ngoài Swiper, góc trên phải) */}
            <button
              onClick={closeViewer}
              className='story-close-btn-global'
              aria-label='Đóng story'
            >
              <svg
                className='h-7 w-7'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>

            {/* Pause indicator (nằm ngoài, trên cùng) */}
            <AnimatePresence>
              {isPaused && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className='story-pause-indicator'
                >
                  <svg
                    className='h-4 w-4'
                    fill='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path d='M6 19h4V5H6v14zm8-14v14h4V5h-4z' />
                  </svg>
                  Đã tạm dừng
                </motion.div>
              )}
            </AnimatePresence>

            {/* Instagram-style Swiper Container */}
            <div className='story-ig-container'>
              <Swiper
                modules={[EffectCreative]}
                effect='creative'
                centeredSlides={true}
                slidesPerView='auto'
                spaceBetween={24}
                allowTouchMove={!isPaused}
                creativeEffect={{
                  limitProgress: 4,
                  prev: {
                    scale: 0.7,
                    translate: ['-90%', 0, 0],
                  },
                  next: {
                    scale: 0.7,
                    translate: ['90%', 0, 0],
                  },
                }}
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                onSlideChange={handleSlideChange}
                className='story-ig-swiper'
              >
                {storiesData.map((item, index) => (
                  <SwiperSlide key={item.id} className='story-ig-slide'>
                    {({ isActive }: { isActive: boolean }) => (
                      <div
                        className={`story-ig-card ${
                          isActive
                            ? 'story-ig-card--active'
                            : 'story-ig-card--inactive'
                        }`}
                        onClick={
                          !isActive
                            ? () => swiperRef.current?.slideTo(index)
                            : undefined
                        }
                      >
                        {/* Progress Bars (chỉ trên slide active) */}
                        {isActive && (
                          <div className='story-progress-wrapper'>
                            {storiesData.map((_, i) => (
                              <div key={i} className='story-progress-track'>
                                <div
                                  ref={(el) => {
                                    progressLinesRef.current[i] = el
                                  }}
                                  className='story-progress-fill'
                                  style={{
                                    width:
                                      i < activeIndex
                                        ? '100%'
                                        : i === activeIndex
                                          ? `${progressRef.current}%`
                                          : '0%',
                                    transition: 'none',
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Header: brand info (chỉ trên slide active) */}
                        {isActive && (
                          <>
                            <div className='story-header'>
                              <div className='flex items-center gap-2.5'>
                                <div className='story-header-avatar'>
                                  <Image
                                    src={optimizeImg(item.preview, 64)}
                                    alt='Coolmate'
                                    width={32}
                                    height={32}
                                    className='h-full w-full rounded-full object-cover'
                                  />
                                </div>
                                <div className='flex flex-col'>
                                  <span className='text-[13px] font-semibold text-white'>
                                    Coolmate
                                  </span>
                                  <span className='text-[11px] text-white/60'>
                                    {activeIndex + 1}/{storiesData.length}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Mute/Unmute Toggle Button (Moved below avatar) */}
                            {item.type === 'video' && (
                              <button
                                className='pointer-events-auto absolute left-[12px] top-[calc(68px+env(safe-area-inset-top,0px))] z-50 rounded-full bg-neutral-900/40 p-1.5 text-white drop-shadow-md backdrop-blur-md transition-all hover:scale-110 hover:bg-black/60 active:scale-95'
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                  const newState = !isMutedRef.current
                                  isMutedRef.current = newState
                                  setIsMuted(newState)
                                  if (videoRefs.current[index]) {
                                    videoRefs.current[index]!.muted = newState
                                  }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                aria-label='Toggle sound'
                              >
                                {isMuted ? (
                                  <svg
                                    className='h-[16px] w-[16px]'
                                    fill='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path d='M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z' />
                                  </svg>
                                ) : (
                                  <svg
                                    className='h-[16px] w-[16px]'
                                    fill='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path d='M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z' />
                                  </svg>
                                )}
                              </button>
                            )}
                          </>
                        )}

                        {/* Video / Image */}
                        {item.type === 'video' ? (
                          <video
                            ref={(el) => {
                              videoRefs.current[index] = el
                            }}
                            src={item.src}
                            poster={optimizeImg(item.preview, 760)}
                            muted={isMuted}
                            playsInline
                            preload='metadata'
                            className='story-media'
                          />
                        ) : (
                          <img
                            src={optimizeImg(item.src, 760)}
                            alt={item.id}
                            className='story-media !object-contain'
                          />
                        )}

                        {/* Gradient overlay */}
                        <div className='story-gradient-bottom' />

                        {/* Product Tags (chỉ trên slide active + không pause) */}
                        {isActive &&
                          !isPaused &&
                          item.productTags.map((tag, tagIdx) => (
                            <motion.div
                              key={`${item.id}-tag-${tagIdx}`}
                              initial={{
                                opacity: 0,
                                scale: 0.8,
                                x: '-50%',
                                y: '-50%',
                              }}
                              animate={{
                                opacity: 1,
                                scale: 1,
                                x: '-50%',
                                y: '-50%',
                              }}
                              exit={{
                                opacity: 0,
                                scale: 0.8,
                                x: '-50%',
                                y: '-50%',
                              }}
                              transition={{
                                type: 'spring',
                                stiffness: 400,
                                damping: 25,
                                delay: 0.2 + tagIdx * 0.1,
                              }}
                              className='story-product-tag'
                              style={{
                                ...getTagPositionStyle(tag.position),
                                willChange: 'transform, opacity',
                              }}
                            >
                              <a
                                href={`/product/${tag.seo_alias}?itm_source=story&itm_medium=story_tag&itm_campaign=story_mode&itm_content=${item.id}`}
                                className='story-tag-body'
                                onClick={(e) => e.stopPropagation()}
                                ga-tracking-value='story_product_tag_click'
                                ga-tracking-label={tag.title}
                              >
                                <div className='story-tag-icon'>
                                  <svg
                                    className='h-4 w-4'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
                                    />
                                  </svg>
                                </div>
                                <div className='flex min-w-0 flex-1 flex-col'>
                                  <span className='text-[12px] font-medium text-neutral-900'>
                                    {tag.title}
                                  </span>
                                  <span className='text-[11px] font-semibold text-neutral-600'>
                                    {priceFormatter.format(tag.regular_price)}
                                  </span>
                                </div>
                                <svg
                                  className='ml-1 h-3.5 w-3.5 text-neutral-400'
                                  fill='none'
                                  stroke='currentColor'
                                  viewBox='0 0 24 24'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2.5}
                                    d='M9 5l7 7-7 7'
                                  />
                                </svg>
                              </a>
                            </motion.div>
                          ))}

                        {/* Touch Layer (chỉ trên slide active) */}
                        {isActive && (
                          <div
                            className='story-touch-layer'
                            onMouseDown={handlePressStart}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={handlePressStart}
                            onTouchEnd={handlePressEnd}
                          >
                            <div
                              className='h-full w-1/4 cursor-pointer'
                              onClick={handleTapLeft}
                            />
                            <div className='h-full w-2/4' />
                            <div
                              className='h-full w-1/4 cursor-pointer'
                              onClick={handleTapRight}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          PHẦN 3: STYLES
          ═══════════════════════════════════════════════════════════════════════ */}
      <style jsx global>{`
        /* ─── Avatar Scroll List ──────────────────────────────────────────── */
        .story-avatar-scroll {
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .story-avatar-scroll::-webkit-scrollbar {
          display: none;
        }

        /* ─── Navigation Arrows ───────────────────────────────────────────── */
        .story-nav-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: white;
          color: #333;
          border: none;
          cursor: pointer;
          box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .story-nav-arrow:hover {
          background: #f5f5f5;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          transform: translateY(-50%) scale(1.08);
        }
        .story-nav-arrow--left {
          left: 6px;
        }
        .story-nav-arrow--right {
          right: 6px;
        }

        /* ─── Viewer Overlay ──────────────────────────────────────────────── */
        .story-viewer-overlay {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(18, 18, 18, 0.97);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }

        /* ─── Global Close Button ─────────────────────────────────────────── */
        .story-close-btn-global {
          position: absolute;
          top: calc(20px + env(safe-area-inset-top, 0px));
          right: 24px;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: none;
          cursor: pointer;
          backdrop-filter: blur(10px);
          transition: all 0.2s ease;
        }
        .story-close-btn-global:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.1);
        }

        /* ─── Giant Pause Indicator ───────────────────────────────────────── */
        .story-pause-indicator {
          position: absolute;
          top: calc(28px + env(safe-area-inset-top, 0px));
          left: 50%;
          transform: translateX(-50%);
          z-index: 100;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          border-radius: 999px;
          background: rgba(0, 0, 0, 0.4);
          color: white;
          font-size: 13px;
          font-weight: 500;
          backdrop-filter: blur(8px);
        }

        /* ─── IG Container \u0026 Swiper ───────────────────────────────────────── */
        .story-ig-container {
          width: 100%;
          max-width: 1200px;
          height: 85vh;
          max-height: 800px;
          position: relative;
        }
        .story-ig-swiper {
          height: 100%;
          overflow: visible !important;
        }
        .story-ig-swiper .swiper-wrapper {
          align-items: center;
        }
        .story-ig-slide {
          width: 380px !important;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (max-width: 768px) {
          .story-ig-slide {
            width: 100% !important;
          }
          .story-ig-container {
            height: 100vh;
            height: 100dvh;
            max-height: 100vh;
            max-height: 100dvh;
          }
          .story-ig-card {
            border-radius: 0;
          }
        }

        .story-ig-card {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 8px;
          overflow: hidden;
          background: #000;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .story-ig-card--active {
          box-shadow:
            0 25px 60px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.08);
        }
        .story-ig-card--inactive {
          cursor: pointer;
        }
        .story-ig-card::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: #000;
          opacity: 0;
          z-index: 25;
          pointer-events: none;
          transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .story-ig-card--inactive::after {
          opacity: 0.6;
        }

        /* ─── Progress Bar ────────────────────────────────────────────────── */
        .story-progress-wrapper {
          position: absolute;
          top: calc(12px + env(safe-area-inset-top, 0px));
          left: 12px;
          right: 12px;
          z-index: 50;
          display: flex;
          gap: 4px;
        }
        .story-progress-track {
          flex: 1;
          height: 2px;
          background: rgba(255, 255, 255, 0.35);
          border-radius: 1px;
          overflow: hidden;
        }
        .story-progress-fill {
          height: 100%;
          background: white;
          border-radius: 1px;
        }

        /* ─── Header ──────────────────────────────────────────────────────── */
        .story-header {
          position: absolute;
          top: calc(28px + env(safe-area-inset-top, 0px));
          left: 12px;
          right: 12px;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 4px;
        }
        .story-header-avatar {
          width: 32px;
          height: 32px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          overflow: hidden;
        }

        /* ─── Media ───────────────────────────────────────────────────────── */
        .story-media {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .story-gradient-bottom {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 200px;
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.6) 0%,
            transparent 100%
          );
          pointer-events: none;
        }

        /* ─── Product Tag ─────────────────────────────────────────────────── */
        .story-product-tag {
          position: absolute;
          z-index: 40;
          pointer-events: auto;
        }
        .story-tag-body {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px 8px 8px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.72);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          text-decoration: none;
          cursor: pointer;
          transition:
            transform 0.2s ease,
            background 0.2s ease;
          width: 100%;
          white-space: normal;
          word-break: break-word;
          user-select: none;
          -webkit-user-select: none;
        }
        .story-tag-body:hover {
          background: rgba(255, 255, 255, 1);
          transform: scale(1.02);
        }
        .story-tag-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #111;
          color: white;
          flex-shrink: 0;
        }

        /* ─── Touch Layer ─────────────────────────────────────────────────── */
        .story-touch-layer {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 30;
          display: flex;
          user-select: none;
          -webkit-user-select: none;
        }
      `}</style>
      <StoryPreloader data={storiesData} />
    </div>
  )
}

// ─── Preloader Component ───────────────────────────────────────────────────
function StoryPreloader({ data }: { data: TStoryItem[] }) {
  return (
    <div className='pointer-events-none hidden' aria-hidden='true'>
      {data.slice(0, 3).map((item) => (
        <React.Fragment key={`preload-${item.id}`}>
          {/* Preload Ảnh preview */}
          <link
            rel='preload'
            as='image'
            href={optimizeImg(item.preview, 128)}
          />
          {/* Preload Video (Chỉ lấy metadata) */}
          {item.type === 'video' && (
            <video src={item.src} preload='metadata' muted />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

export default StoryMode
